import { useModal, create, show } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import FormFieldTextConfirm from "components/FormFields/Confirm";
import Box from "ds/components/Box";

import { DELETE_SCIM_OAUTH_CLIENT } from "../gql";

const useDeleteScim = createDefaultMutationHook(DELETE_SCIM_OAUTH_CLIENT);

const SettingsSingleSignOnDeleteScim = create(function SettingsSingleSignOnDeleteScim() {
  const modal = useModal();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSSO,
    defaultCallbackTrackProperties: { method: "SCIM" },
  });

  const { reportSuccess } = useTypedContext(FlashContext);

  const [deleteScim, { loading, data }] = useDeleteScim({
    refetchQueries: ["GetSSOSettings"],
  });

  const handleDelete = () => {
    trackSegmentAnalyticsEvent("SCIM Revoke Confirm Click");
    deleteScim({}, (data) => {
      if (data?.deleteOauthClientForSCIM) {
        reportSuccess({ message: "SCIM credentials revoked" });
        modal.hide();
      }
    });
  };

  const form = useForm<{ deleteConfirmationText: string }>({
    defaultValues: { deleteConfirmationText: "" },
    mode: "onChange",
  });

  return (
    <DeleteConfirmationModal
      title="Revoke SCIM credentials"
      onConfirm={handleDelete}
      isLoading={loading}
      isDismissable={!loading && !data?.deleteOauthClientForSCIM}
      confirmationButtonLabel="Revoke"
      isConfirmationDisabled={loading || !form.formState.isValid}
    >
      <Box fullWidth padding="0 0 large 0">
        <ConfirmationModalMessage>
          Are you sure you want to revoke the SCIM OAuth credentials? Any SCIM integrations using
          these credentials will stop working.
        </ConfirmationModalMessage>
      </Box>

      <FormProvider {...form}>
        <FormFieldTextConfirm
          fieldName="deleteConfirmationText"
          textToConfirm="revoke"
          disabled={loading}
        />
      </FormProvider>
    </DeleteConfirmationModal>
  );
});

export const showDeleteScimConfirmation = () => show(SettingsSingleSignOnDeleteScim);
